import { useEffect, useState } from 'react';
import {
  useApi,
  identityApiRef,
  oktaAuthApiRef,
  SessionState,
} from '@backstage/core-plugin-api';

interface UseOktaAuthGuardResult {
  isAuthenticated: boolean | null;
  isGuest: boolean;
  isLoading: boolean;
}

/**
 * Hook to check if the user is authenticated with Okta.
 * Returns authentication state and whether the user is a guest.
 */
export const useOktaAuthGuard = (): UseOktaAuthGuardResult => {
  const identityApi = useApi(identityApiRef);
  const oktaAuthApi = useApi(oktaAuthApiRef);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isGuest, setIsGuest] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;
    let subscription: any;

    const checkAuthentication = async () => {
      try {
        // Check if user is a guest by examining their identity
        const backstageIdentity = await identityApi.getBackstageIdentity();
        const userEntityRef = backstageIdentity.userEntityRef;
        const isGuestUser = userEntityRef === 'user:default/guest';

        if (mounted) {
          setIsGuest(isGuestUser);
        }

        // If guest user, they're not authenticated with Okta
        if (isGuestUser) {
          if (mounted) {
            setIsAuthenticated(false);
            setIsLoading(false);
          }
          return;
        }

        // Check Okta authentication state
        try {
          await oktaAuthApi.getProfile();
          if (mounted) {
            setIsAuthenticated(true);
            setIsLoading(false);
          }
        } catch (error) {
          if (mounted) {
            setIsAuthenticated(false);
            setIsLoading(false);
          }
        }

        // Subscribe to session state changes
        subscription = oktaAuthApi.sessionState$().subscribe(sessionState => {
          if (mounted) {
            setIsAuthenticated(sessionState === SessionState.SignedIn);
          }
        });
      } catch (error) {
        // If we can't get identity at all, user is not authenticated
        if (mounted) {
          setIsAuthenticated(false);
          setIsGuest(false);
          setIsLoading(false);
        }
      }
    };

    checkAuthentication();

    return () => {
      mounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [identityApi, oktaAuthApi]);

  return {
    isAuthenticated,
    isGuest,
    isLoading,
  };
};
